/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * TablePagination — Premium pagination with round buttons & brand active state.
 *
 * Features: round page number buttons, brand-glow active page, hover lift,
 * styled page-size selector, responsive mobile layout, cubic-bezier easing,
 * prefers-reduced-motion support.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

interface TablePaginationProps {
  page: number;
  pageSize: number;
  totalElements: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  pageSizeOptions?: number[];
}

export default function TablePagination({
  page,
  pageSize,
  totalElements,
  totalPages,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 50],
}: TablePaginationProps) {
  const { t } = useTranslation('venta');

  const showFrom = totalElements > 0 ? page * pageSize + 1 : 0;
  const showTo = Math.min((page + 1) * pageSize, totalElements);

  const pageNumbers = useMemo(() => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i);
    }
    const pages: (number | 'ellipsis')[] = [];
    pages.push(0);
    if (page > 2) pages.push('ellipsis');
    for (let i = Math.max(1, page - 1); i <= Math.min(totalPages - 2, page + 1); i++) {
      pages.push(i);
    }
    if (page < totalPages - 3) pages.push('ellipsis');
    pages.push(totalPages - 1);
    return pages;
  }, [page, totalPages]);

  if (totalElements === 0) return null;

  return (
    <>
      <style>{pgStyles}</style>
      <div className="pg-root">
        <div className="pg-info">
          <span className="pg-showing">
            {t('venta.common.showing', {
              from: showFrom,
              to: showTo,
              total: totalElements,
              defaultValue: 'Showing {{from}}-{{to}} of {{total}}',
            })}
          </span>
          <div className="pg-size">
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="pg-select"
            >
              {pageSizeOptions.map(size => (
                <option key={size} value={size}>
                  {size} / {t('venta.common.page', { defaultValue: 'page' })}
                </option>
              ))}
            </select>
          </div>
        </div>

        {totalPages > 1 && (
          <div className="pg-nav">
            <button
              type="button"
              className="pg-btn pg-arrow"
              disabled={page === 0}
              onClick={() => onPageChange(0)}
              title={t('venta.common.firstPage', { defaultValue: 'First page' })}
            >
              <ChevronsLeft size={14} />
            </button>
            <button
              type="button"
              className="pg-btn pg-arrow"
              disabled={page === 0}
              onClick={() => onPageChange(page - 1)}
              title={t('venta.common.prevPage', { defaultValue: 'Previous' })}
            >
              <ChevronLeft size={14} />
            </button>

            {pageNumbers.map((p, idx) =>
              p === 'ellipsis' ? (
                <span key={`ellipsis-${idx}`} className="pg-dots">...</span>
              ) : (
                <button
                  key={p}
                  type="button"
                  className={`pg-btn pg-num ${p === page ? 'pg-active' : ''}`}
                  onClick={() => onPageChange(p)}
                >
                  {p + 1}
                </button>
              )
            )}

            <button
              type="button"
              className="pg-btn pg-arrow"
              disabled={page >= totalPages - 1}
              onClick={() => onPageChange(page + 1)}
              title={t('venta.common.nextPage', { defaultValue: 'Next' })}
            >
              <ChevronRight size={14} />
            </button>
            <button
              type="button"
              className="pg-btn pg-arrow"
              disabled={page >= totalPages - 1}
              onClick={() => onPageChange(totalPages - 1)}
              title={t('venta.common.lastPage', { defaultValue: 'Last page' })}
            >
              <ChevronsRight size={14} />
            </button>
          </div>
        )}
      </div>
    </>
  );
}

const pgStyles = `
  .pg-root {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    font-size: 13px;
    color: var(--color-text-muted);
    flex-wrap: wrap;
  }

  .pg-info {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  /* Size selector */
  .pg-select {
    height: 32px;
    padding: 0 10px;
    font-size: 12px;
    font-weight: 500;
    color: var(--color-text-primary);
    background: var(--color-bg-tertiary);
    border: 1.5px solid var(--color-border-default, rgba(255,255,255,0.08));
    border-radius: 3px;
    outline: none;
    cursor: pointer;
    transition: border-color 200ms ease, box-shadow 200ms ease;
  }
  .pg-select:focus {
    border-color: var(--color-brand-primary);
    box-shadow: 0 0 0 2px var(--color-brand-glow, rgba(139,0,0,0.15));
  }

  .pg-nav {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  /* Shared button base */
  .pg-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    cursor: pointer;
    font-size: 12px;
    font-weight: 600;
    color: var(--color-text-primary);
    transition:
      background 180ms ease,
      transform 200ms cubic-bezier(0.16,1,0.3,1),
      box-shadow 200ms ease;
  }

  .pg-btn:disabled {
    opacity: 0.3;
    cursor: not-allowed;
    transform: none !important;
  }

  /* Arrow buttons (first/prev/next/last) */
  .pg-arrow {
    width: 32px;
    height: 32px;
    background: var(--color-bg-tertiary);
    border-radius: 3px;
  }
  .pg-arrow:not(:disabled):hover {
    background: var(--color-bg-hover);
    transform: translateY(-1px);
  }
  .pg-arrow:not(:disabled):active {
    transform: translateY(0) scale(0.95);
  }

  /* Page number buttons */
  .pg-num {
    min-width: 32px;
    height: 32px;
    padding: 0 4px;
    background: transparent;
    border-radius: 3px;
  }
  .pg-num:not(.pg-active):not(:disabled):hover {
    background: var(--color-bg-hover);
    transform: translateY(-1px);
  }
  .pg-num:not(.pg-active):not(:disabled):active {
    transform: scale(0.92);
  }

  /* Active page */
  .pg-num.pg-active {
    background: var(--color-brand-primary);
    color: #fff;
    box-shadow: 0 0 10px var(--color-brand-glow, rgba(139,0,0,0.3));
    transform: scale(1.05);
  }

  .pg-dots {
    min-width: 24px;
    text-align: center;
    font-size: 12px;
    color: var(--color-text-muted);
    user-select: none;
  }

  /* Mobile */
  @media (max-width: 600px) {
    .pg-root {
      flex-direction: column;
      align-items: stretch;
    }
    .pg-info { justify-content: space-between; }
    .pg-nav { justify-content: center; }
  }

  /* Reduced motion */
  @media (prefers-reduced-motion: reduce) {
    .pg-btn,
    .pg-select {
      transition-duration: 0ms !important;
    }
  }
`;
